import React, { useState, useRef, useEffect } from 'react';
import './FileInputPanel.css';

const STORAGE_KEY = 'arabcap_editor_data';

const FileInputPanel = ({ onLoadData, disableAutoLoad = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonFile, setJsonFile] = useState(null);
  const [mediaFile, setMediaFile] = useState(null);
  const [inputMode, setInputMode] = useState('file'); // 'file' or 'paste'
  const [error, setError] = useState(null);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  
  const jsonFileInputRef = useRef(null);
  const mediaFileInputRef = useRef(null);

  // Load saved data from localStorage on mount
  useEffect(() => {
    let mounted = true;
    if (disableAutoLoad) return () => {
      mounted = false;
    };

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && mounted) {
        const { jsonData, timestamp } = JSON.parse(saved);
        if (jsonData) {
          setJsonText(JSON.stringify(jsonData, null, 2));
          // Auto-load the saved data
          onLoadData({
            captionData: jsonData,
            mediaUrl: null,
            mediaFile: null
          });
          setHasLoadedData(true);
        }
      }
    } catch (err) {
      console.error('Failed to load saved data:', err);
    }
    
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleJsonFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setJsonFile(file);
      setError(null);
      
      // Read and validate the file
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target.result;
          JSON.parse(text); // Validate JSON
          setJsonText(text);
        } catch (err) {
          setError('Invalid JSON file: ' + err.message);
        }
      };
      reader.onerror = () => {
        setError('Failed to read JSON file');
      };
      reader.readAsText(file);
    }
  };

  const handleJsonTextChange = (e) => {
    setJsonText(e.target.value);
    setError(null);
  };

  const handleMediaFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['video/mp4', 'video/webm', 'video/ogg', 'audio/mpeg', 'audio/wav', 'audio/ogg'];
      if (!validTypes.includes(file.type)) {
        setError('Please select a valid video or audio file');
        return;
      }
      setMediaFile(file);
      setError(null);
    }
  };

  const handleLoad = () => {
    setError(null);
    
    // Validate JSON
    if (!jsonText) {
      setError('Please provide JSON data');
      return;
    }
    
    let parsedData;
    try {
      parsedData = JSON.parse(jsonText);
    } catch (err) {
      setError('Invalid JSON: ' + err.message);
      return;
    }
    
    if (!parsedData.skeleton || !parsedData.words) {
      setError('JSON must contain "skeleton" and "words" properties');
      return;
    }
    
    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        jsonData: parsedData,
        timestamp: Date.now()
      }));
    } catch (err) {
      console.error('Failed to save to localStorage:', err);
    }
    
    // Create media URL if file is provided
    let mediaUrl = null;
    if (mediaFile) {
      mediaUrl = URL.createObjectURL(mediaFile);
    }
    
    // Call parent handler
    onLoadData({
      captionData: parsedData,
      mediaUrl: mediaUrl,
      mediaFile: mediaFile
    });
    
    setHasLoadedData(true);
    setIsExpanded(false); // Collapse after loading
  };

  const handleExportJson = () => {
    // This will be called from parent, but we can provide a helper
    const dataStr = jsonText;
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'captions.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`file-input-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="file-input-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
            <polyline points="13 2 13 9 20 9" />
          </svg>
          <span className="header-title">
            {hasLoadedData ? 'Data Loaded' : 'Load Caption Data'}
          </span>
          {hasLoadedData && (
            <span className="header-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
          )}
        </div>
        <div className="header-right">
          {jsonText && !isExpanded && (
            <button 
              className="header-action-btn tooltip-trigger" 
              onClick={(e) => { e.stopPropagation(); handleExportJson(); }}
              data-tooltip="Export JSON"
              aria-label="Export JSON"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          )}
          <button className="toggle-button" onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}>
            <svg 
              width="14" 
              height="14" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="file-input-content">
          <div className="file-input-section">
            <div className="input-mode-toggle">
              <button
                className={inputMode === 'file' ? 'active' : ''}
                onClick={() => setInputMode('file')}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Upload JSON
              </button>
              <button
                className={inputMode === 'paste' ? 'active' : ''}
                onClick={() => setInputMode('paste')}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
                Paste JSON
              </button>
            </div>

            {inputMode === 'file' ? (
              <div className="file-input-group">
                <input
                  ref={jsonFileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleJsonFileChange}
                  style={{ display: 'none' }}
                />
                <button
                  className="file-select-button"
                  onClick={() => jsonFileInputRef.current?.click()}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z" />
                    <polyline points="13 2 13 9 20 9" />
                  </svg>
                  {jsonFile ? jsonFile.name : 'Select JSON File'}
                </button>
              </div>
            ) : (
              <div className="json-paste-area">
                <textarea
                  placeholder="Paste your JSON data here..."
                  value={jsonText}
                  onChange={handleJsonTextChange}
                  rows={8}
                />
              </div>
            )}
          </div>

          <div className="file-input-section">
            <label>Media File (Optional)</label>
            <input
              ref={mediaFileInputRef}
              type="file"
              accept="video/*,audio/*"
              onChange={handleMediaFileChange}
              style={{ display: 'none' }}
            />
            <button
              className="file-select-button"
              onClick={() => mediaFileInputRef.current?.click()}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
              {mediaFile ? mediaFile.name : 'Select Video/Audio'}
            </button>
          </div>

          {error && <div className="file-input-error">{error}</div>}

          <div className="file-input-actions">
            <button className="load-button" onClick={handleLoad}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Load Data
            </button>
            {jsonText && (
              <button className="export-button" onClick={handleExportJson}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Export JSON
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileInputPanel;
